<template>
  <el-form
    :model="applicationForm"
    label-width="150px"
    label-position="top"
    require-asterisk-position="right"
    @submit.prevent="handleSubmit">
    <div data-tutorial-id="application-inputs">
      <!-- Name -->
      <el-form-item
        :label="i18n.t('name')"
        prop="display_name"
        :error="getErrorMessage('display_name')"
        required>
        <SmInput v-model="applicationForm.fields.display_name" design="form" />
      </el-form-item>
      <!-- Short Description -->
      <el-form-item
        :label="
          i18n.t('drawers.editSoftwareDrawer.form.shortDescription.label')
        "
        prop="description_short">
        <SmInput v-model="applicationForm.fields.desc_short" design="form" />
      </el-form-item>

      <!-- Description -->
      <el-form-item
        :label="i18n.t('drawers.editSoftwareDrawer.form.description.label')"
        prop="description">
        <SmInput
          v-model="applicationForm.fields.desc"
          :autosize="{ minRows: 2, maxRows: 10 }"
          type="area"
          design="form" />
      </el-form-item>

      <!-- Owner -->
      <el-form-item
        :label="i18n.t('drawers.editSoftwareDrawer.form.owner.label')"
        prop="owner">
        <SmSelect
          v-model:selection="ownerSelection"
          :options="peopleOptions"
          :label="i18n.t('selectUser')"
          :item-size="66"
          :loading="loadingPeopleOptions"
          class="w-full"
          multiselect
          searchable>
          <template #item="{ item }">
            <div class="flex flex-col leading-6">
              <span>{{ item.value.name }}</span>
              <span class="text-contrast-muted">{{ item.value.email }}</span>
            </div>
          </template>
        </SmSelect>
      </el-form-item>
    </div>
    <div class="mt-6 flex justify-end">
      <SmButton
        outline
        :loading="applicationForm.processing"
        @click="handleCancelButtonClick">
        {{ i18n.t('general.cancel') }}
      </SmButton>
      <SmButton
        type="primary"
        :loading="applicationForm.processing"
        @click="handleSaveButtonClick">
        {{ i18n.t('general.save') }}
      </SmButton>
    </div>
  </el-form>
</template>
<script lang="ts" setup>
  import { computed, onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  import {
    Account,
    SoftwareOut,
    SoftwarePostIn,
    SoftwareTypes,
    SoftwareUpdateIn,
  } from '@/client'
  import useForm from '@/composables/useForm'
  import { Option } from '../sm/SmSelect.vue'

  import { ValidationResult, isRequired } from '../sm/SmInput/SmInputValidator'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { PersonStore } from '@/stores/person/person.store'

  const i18n = useI18n()

  type ApplicationFormFields = {
    display_name: string
    desc_short?: string
    desc?: string
    notes?: string
    owner: string[]
  }

  // Props and Emits
  const props = defineProps({
    applicationData: {
      type: Object as PropType<SoftwareOut>,
      default: undefined,
    },
  })

  const emit = defineEmits<{
    cancel: []
    save: []
  }>()

  const isCreate = computed(() => {
    return !props.applicationData
  })

  const formFields: ApplicationFormFields = {
    display_name: props.applicationData?.display_name ?? '',
    desc_short: props.applicationData?.desc_short ?? '',
    desc: props.applicationData?.desc ?? '',
    notes: props.applicationData?.notes ?? '',
    owner: props.applicationData?.owner_account_ids ?? [],
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  Create application
  // ════════════════════════════════════════════════════════════════════════════

  async function createApplication() {
    await applicationForm.submit(async (fields) => {
      const { owner, ...rest } = fields

      const payload: SoftwarePostIn = {
        ...rest,
        owner_account_ids: owner.length ? owner : undefined,
        software_name: rest.display_name,

        type: SoftwareTypes.MANUALLY,
      }

      // convert empty string to undefined
      for (const [key, value] of Object.entries(payload)) {
        if (value === '') (payload as any)[key] = undefined
      }

      return await ApplicationStore.createApplication({ requestBody: payload })
    })
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  Update application
  // ════════════════════════════════════════════════════════════════════════════

  async function updateApplication() {
    await applicationForm.submit(async (fields) => {
      if (!props.applicationData?._id) throw new Error('Application not found')

      const { owner, ...rest } = fields

      const payload: SoftwareUpdateIn = {
        ...rest,
        owner_account_ids: owner,
      }

      // convert empty string to null
      for (const [key, value] of Object.entries(payload)) {
        if (value === '') (payload as any)[key] = null
      }

      return await ApplicationStore.updateApplication(
        props.applicationData._id,
        payload
      )
    })
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  Form
  // ════════════════════════════════════════════════════════════════════════════
  const peopleOptions = ref<Option<Account>[]>([])
  const loadingPeopleOptions = ref<boolean>(false)
  const peopleOptionById = ref(new Map<string, Option<Account>>())

  const applicationForm = useForm(
    formFields,
    {},
    { display_name: [isRequired] },
    { display_name: i18n.t('name') }
  )

  const ownerSelection = computed<Option<Account>[]>({
    get() {
      if (!applicationForm.fields.owner.length) return []
      const map = peopleOptionById.value
      return applicationForm.fields.owner
        .map((id) => map.get(id))
        .filter((option): option is Option<Account> => Boolean(option))
    },
    set(options) {
      if (!Array.isArray(options) || options.length === 0) {
        applicationForm.fields.owner = []
        return
      }

      applicationForm.fields.owner = options.map((option) => option.value._id)
    },
  })

  function getErrorMessage(fieldName: keyof ApplicationFormFields) {
    if (!((fieldName as string) in applicationForm.errors)) return ''
    const errors = applicationForm.errors[fieldName] as ValidationResult[]
    if (!errors || errors.length === 0 || !errors[0] || !errors[0].message)
      return ''
    const message = errors[0].message as string
    return message
  }

  async function loadAccountSelection() {
    loadingPeopleOptions.value = true

    const persons = await PersonStore.getPersons<Account>(
      { isDeleted: false },
      {},
      {},
      false
    )

    peopleOptions.value = persons.map((account) => ({
      value: account,
      label: account.name as string,
      key: account._id,
    }))
    peopleOptionById.value = new Map(
      peopleOptions.value.map((option) => [option.value._id, option])
    )

    loadingPeopleOptions.value = false
  }

  onMounted(() => {
    loadAccountSelection()
  })

  // ════════════════════════════════════════════════════════════════════════════
  //  Action Handler
  // ════════════════════════════════════════════════════════════════════════════

  async function handleSaveButtonClick() {
    if (applicationForm.validate()) {
      await handleSubmit()
    }
  }

  async function handleCancelButtonClick() {
    emit('cancel')
  }

  async function handleSubmit() {
    if (isCreate.value) {
      await createApplication()
    } else {
      await updateApplication()
    }
    // Software Saved
    await ApplicationStore.updateApplicationCacheFromApi()

    emit('save')
  }
</script>
